import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Stack,
  Chip,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
  Card,
  CardContent,
  Slider,
  LinearProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridPaginationModel,
} from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import ListIcon from '@mui/icons-material/List'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { Oportunidade, OportunidadeEstagio } from '@/types/oportunidade'
import { formatCurrency, formatDate } from '@/utils/formatters'
import { oportunidadeService } from '@/services/oportunidadeService'
import { useAuth } from '@/hooks/useAuth'

const estagios: OportunidadeEstagio[] = ['DIAGNOSTICO', 'ESTRUTURACAO', 'NEGOCIACAO', 'CONTRATADA']

const estagioLabelMap: Record<OportunidadeEstagio, string> = {
  DIAGNOSTICO: 'Diagnóstico',
  ESTRUTURACAO: 'Estruturação',
  NEGOCIACAO: 'Negociação',
  CONTRATADA: 'Contratada',
}

const estagioColorMap: Record<OportunidadeEstagio, 'info' | 'warning' | 'success'> = {
  DIAGNOSTICO: 'info',
  ESTRUTURACAO: 'warning',
  NEGOCIACAO: 'warning',
  CONTRATADA: 'success',
}

const OportunidadesListPage: React.FC = () => {
  const navigate = useNavigate()
  const [oportunidades, setOportunidades] = useState<Oportunidade[]>([])
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'lista' | 'kanban'>('lista')
  const [searchText, setSearchText] = useState('')
  const [selectedEstagios, setSelectedEstagios] = useState<OportunidadeEstagio[]>(estagios)
  const [valorMin, setValorMin] = useState<number>(0)
  const [valorMax, setValorMax] = useState<number>(10000000)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  })
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [oportunidadeToDelete, setOportunidadeToDelete] = useState<Oportunidade | null>(null)

  // Exclusão física é ADMIN (espelha o @PreAuthorize do endpoint; o backend é a
  // autoridade). Os demais perfis encerram a reserva pela máquina de status.
  const { usuario } = useAuth()
  const podeExcluir = useMemo(
    () => (usuario?.permissoes ?? []).includes('ADMIN'),
    [usuario?.permissoes],
  )

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    try {
      const page = await oportunidadeService.listar(paginationModel.page, paginationModel.pageSize)
      setOportunidades(page.content ?? [])
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar oportunidades')
      setOportunidades([])
    } finally {
      setLoading(false)
    }
  }, [paginationModel.page, paginationModel.pageSize])

  useEffect(() => {
    carregar()
  }, [carregar])

  const filteredOportunidades = useMemo(() => {
    return oportunidades.filter((opu) => {
      const matchesSearch =
        opu.nomeCliente?.toLowerCase().includes(searchText.toLowerCase()) ||
        opu.tipoNecessidade.toLowerCase().includes(searchText.toLowerCase())

      const matchesEstagio = selectedEstagios.includes(opu.estagio)
      const matchesValor = opu.valorEstimado >= valorMin && opu.valorEstimado <= valorMax

      return matchesSearch && matchesEstagio && matchesValor
    })
  }, [oportunidades, searchText, selectedEstagios, valorMin, valorMax])

  const handleAddOportunidade = useCallback(() => {
    navigate('/oportunidades/nova')
  }, [navigate])

  const handleEditOportunidade = useCallback((opu: Oportunidade) => {
    navigate(`/oportunidades/${opu.id}/editar`, { state: { oportunidade: opu } })
  }, [navigate])

  const handleViewOportunidade = useCallback((opuId: string) => {
    navigate(`/oportunidades/${opuId}`)
  }, [navigate])

  const handleDeleteClick = useCallback((opu: Oportunidade) => {
    setOportunidadeToDelete(opu)
    setDeleteConfirmOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(async () => {
    if (!oportunidadeToDelete) return
    setLoading(true)
    try {
      await oportunidadeService.deletar(oportunidadeToDelete.id)
      setOportunidades((prev) => prev.filter((o) => o.id !== oportunidadeToDelete.id))
      setDeleteConfirmOpen(false)
      setOportunidadeToDelete(null)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao deletar oportunidade')
    } finally {
      setLoading(false)
    }
  }, [oportunidadeToDelete])

  const handleEstagioChange = useCallback((estagio: OportunidadeEstagio) => {
    setSelectedEstagios((prev) =>
      prev.includes(estagio)
        ? prev.filter((e) => e !== estagio)
        : [...prev, estagio],
    )
  }, [])

  const columns: GridColDef[] = [
    {
      field: 'nomeCliente',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          component="span"
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={() => handleViewOportunidade(params.row.id)}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'tipoNecessidade',
      headerName: 'Tipo de Necessidade',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'valorEstimado',
      headerName: 'Valor Estimado',
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        formatCurrency(params.value),
    },
    {
      field: 'estagio',
      headerName: 'Estágio',
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={estagioLabelMap[params.value as OportunidadeEstagio]}
          color={estagioColorMap[params.value as OportunidadeEstagio]}
          size="small"
        />
      ),
    },
    {
      // Aderência substituiu a probabilidade digitada (decisão 6.3) — calculada, 0–100.
      field: 'scoreAderencia',
      headerName: 'Aderência',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 80 }}>
            <LinearProgress
              variant="determinate"
              value={params.value ?? 0}
              sx={{
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: (params.value ?? 0) > 70 ? '#4caf50' : (params.value ?? 0) > 40 ? '#ff9800' : '#f44336',
                },
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ minWidth: 40 }}>
            {params.value ?? 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Data',
      width: 130,
      renderCell: (params: GridRenderCellParams) => formatDate(params.value),
    },
    {
      field: 'acoes',
      headerName: 'Ações',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={1}>
          <EditIcon
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              fontSize: 20,
              '&:hover': { opacity: 0.7 },
            }}
            onClick={() => handleEditOportunidade(params.row)}
          />
          {podeExcluir && (
            <DeleteIcon
              sx={{
                cursor: 'pointer',
                color: 'error.main',
                fontSize: 20,
                '&:hover': { opacity: 0.7 },
              }}
              onClick={() => handleDeleteClick(params.row)}
            />
          )}
        </Stack>
      ),
    },
  ]

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" fontWeight={600}>
          Oportunidades
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddOportunidade}
        >
          Nova Oportunidade
        </Button>
      </Stack>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar as oportunidades. {erro}
        </Alert>
      )}

      {/* View Toggle */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_e, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="lista" aria-label="lista">
            <ListIcon />
          </ToggleButton>
          <ToggleButton value="kanban" aria-label="kanban">
            <ViewWeekIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <TextField
            placeholder="Buscar por cliente ou tipo..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Estágios
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {estagios.map((estagio) => (
                <Chip
                  key={estagio}
                  label={estagioLabelMap[estagio]}
                  onClick={() => handleEstagioChange(estagio)}
                  variant={selectedEstagios.includes(estagio) ? 'filled' : 'outlined'}
                  color={selectedEstagios.includes(estagio) ? estagioColorMap[estagio] : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Valor Estimado
            </Typography>
            <Slider
              value={[valorMin, valorMax]}
              onChange={(_, newValue) => {
                setValorMin((newValue as number[])[0])
                setValorMax((newValue as number[])[1])
              }}
              min={0}
              max={10000000}
              step={100000}
              marks={[
                { value: 0, label: 'R$ 0' },
                { value: 10000000, label: 'R$ 10M' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => formatCurrency(value)}
            />
          </Box>
        </Stack>
      </Paper>

      {/* Vista em Lista */}
      {viewMode === 'lista' && (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredOportunidades}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25]}
            loading={loading}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
              },
            }}
          />
        </Paper>
      )}

      {/* Vista em Kanban */}
      {viewMode === 'kanban' && (
        <Grid container spacing={2}>
          {estagios.map((estagio) => (
            <Grid item xs={12} sm={6} md={3} key={estagio}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  {estagioLabelMap[estagio]}
                  <Chip
                    label={filteredOportunidades.filter((o) => o.estagio === estagio).length}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Stack spacing={1}>
                  {filteredOportunidades
                    .filter((o) => o.estagio === estagio)
                    .map((opu) => (
                      <Card
                        key={opu.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { boxShadow: 3 },
                          transition: 'all 0.2s',
                        }}
                        onClick={() => handleViewOportunidade(opu.id)}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600} noWrap>
                            {opu.nomeCliente}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {opu.tipoNecessidade}
                          </Typography>
                          <Typography variant="body1" fontWeight={600} sx={{ mt: 1, mb: 1 }}>
                            {formatCurrency(opu.valorEstimado)}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="textSecondary">
                              Aderência: {opu.scoreAderencia ?? 0}/100
                            </Typography>
                            <Stack direction="row" spacing={0.5}>
                              <EditIcon
                                sx={{ fontSize: 16, cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleEditOportunidade(opu)
                                }}
                              />
                              {podeExcluir && (
                                <DeleteIcon
                                  sx={{ fontSize: 16, cursor: 'pointer', color: 'error.main' }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDeleteClick(opu)
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja deletar a oportunidade de{' '}
            <strong>{oportunidadeToDelete?.nomeCliente}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default OportunidadesListPage
